import { useState, useEffect, useRef } from "react";

/* ───────────────────────────────────────────────
   DATA
─────────────────────────────────────────────── */

const localHoneyImage = (fileName) => `/images/products/${fileName}`;

const NAV_LINKS = [
  { id: "home", label: "Home" },
  { id: "about", label: "About" },
  { id: "mission", label: "Mission" },
  { id: "pilot", label: "Pilot" },
  { id: "products", label: "Products" },
  { id: "model", label: "Distribution" },
  { id: "structure", label: "Organisation" },
  { id: "warehouse", label: "Warehouse" },
  { id: "ecommerce", label: "E-Commerce" },
  { id: "weaving", label: "Weaving" },
  { id: "finance", label: "Finance" },
  { id: "revenue", label: "Revenue" },
  { id: "team", label: "Team" },
];

const MISSION_OBJECTIVES = [
  { icon: "👩", text: "Economically empower women SHGs and tribal communities" },
  { icon: "🧵", text: "Revive traditional weaving and artisan industries of West Bengal" },
  { icon: "🚫", text: "Eliminate exploitative middlemen structures" },
  { icon: "🔗", text: "Create direct market linkages between producers and consumers" },
  { icon: "🏭", text: "Establish sustainable warehousing and logistics infrastructure" },
  { icon: "💻", text: "Create a scalable E-Commerce ecosystem for rural India" },
  { icon: "💼", text: "Generate employment for women at both production and distribution levels" },
  { icon: "🌱", text: "Facilitate entrepreneurship and equity participation" },
  { icon: "🏦", text: "Enable institutional financing and long-term sustainability" },
  { icon: "🏷️", text: "Create a nationally recognized ethical rural products brand" },
];

const PRODUCT_CATEGORIES = [
  {
    id: "honey",
    label: "Sundarbans Honey",
    emoji: "🍯",
    accent: "#9DCD43",
    products: [
      {
        id: 1,
        name: "Sundarbans Forest Honey",
        price: 349,
        img: localHoneyImage("honeyraw.jpg"),
        cat: "Sundarbans Honey",
        tag: "Mangrove Honey",
        desc: "Natural forest honey inspired by the Sundarbans mangrove belt.",
        options: ["Raw", "Filtered", "Gift Jar"],
        optionImages: {
          Raw: localHoneyImage("honeyraw.jpg"),
          Filtered: localHoneyImage("honeyfilter.jpg"),
          "Gift Jar": localHoneyImage("honeygifted.jpg"),
        },
      },
      {
        id: 2,
        name: "Wildflower Honey",
        price: 299,
        img: localHoneyImage("wildflower.jpg"),
        cat: "Sundarbans Honey",
        tag: "Floral Honey",
        desc: "A floral honey range suitable for tea, breakfast and gifting.",
        options: ["Wildflower", "Light", "Premium"],
        optionImages: {
          Wildflower: localHoneyImage("wildflower.jpg"),
          Light: localHoneyImage("wildflowerlight.jpg"),
          Premium: localHoneyImage("wildflower.jpg"),
        },
      },
      {
        id: 3,
        name: "Mustard Blossom Honey",
        price: 329,
        img: localHoneyImage("mustardblossom.jpg"),
        cat: "Sundarbans Honey",
        tag: "Bengal Fields",
        desc: "Honey inspired by Bengal's seasonal mustard flower fields.",
        options: ["Regular", "Gift Pack", "Family Pack"],
        optionImages: {
          Regular: localHoneyImage("mustardblossom.jpg"),
          "Gift Pack": localHoneyImage("mustardgift.jpg"),
          "Family Pack": localHoneyImage("mustardgift.jpg"),
        },
      },
      {
        id: 4,
        name: "Raw Honeycomb Pack",
        price: 449,
        img: localHoneyImage("rawcomboboxs.jpg"),
        cat: "Sundarbans Honey",
        tag: "Raw & Natural",
        desc: "Premium honeycomb-style product for natural food buyers.",
        options: ["Honeycomb Box", "Raw Combo", "Gift Box"],
        optionImages: {
          "Honeycomb Box": localHoneyImage("rawcomboboxs.jpg"),
          "Raw Combo": localHoneyImage("rawhoneycombo.jpg"),
          "Gift Box": localHoneyImage("rawcomboboxs.jpg"),
        },
      },
      {
        id: 5,
        name: "Herbal Honey Infusion",
        price: 399,
        img: localHoneyImage("herbalhoney.jpg"),
        cat: "Sundarbans Honey",
        tag: "Wellness Honey",
        desc: "Honey blended with ginger, tulsi or herbal flavours.",
        options: ["Herbal", "Ginger", "Tulsi"],
        optionImages: {
          Herbal: localHoneyImage("herbalhoney.jpg"),
          Ginger: localHoneyImage("Ginger-Honey.jpg"),
          Tulsi: localHoneyImage("tulsihoney.jpg"),
        },
      },
    ],
  },

  {
    id: "spices",
    label: "Bengal Spices & Food",
    emoji: "🌶️",
    accent: "#B4E065",
    products: [
      {
        id: 6,
        name: "Panch Phoron Spice Mix",
        price: 149,
        img: "https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=900&q=80",
        cat: "Bengal Spices",
        tag: "Five Spice",
        desc: "Classic Bengali five-spice blend — fenugreek, nigella, cumin, black mustard and fennel.",
        options: ["Regular", "Premium", "Bulk"],
      },
      {
        id: 7,
        name: "Black Mustard Seeds",
        price: 120,
        img: "https://images.unsplash.com/photo-1599909533731-0dcbf2fc8c0a?w=900&q=80",
        cat: "Bengal Spices",
        tag: "Bengali Essential",
        desc: "Essential spice used widely in Bengali kitchens for tempering and pickling.",
        options: ["250g", "500g", "1kg"],
      },
      {
        id: 8,
        name: "Turmeric Powder",
        price: 135,
        img: "https://images.unsplash.com/photo-1615485500704-8e990f9900f0?w=900&q=80",
        cat: "Bengal Spices",
        tag: "Natural Colour",
        desc: "Clean, bright turmeric powder for everyday cooking and wellness.",
        options: ["250g", "500g", "1kg"],
      },
      {
        id: 9,
        name: "Red Chilli Powder",
        price: 145,
        img: "https://images.unsplash.com/photo-1583119022894-919a68a3d0e3?w=900&q=80",
        cat: "Bengal Spices",
        tag: "Spicy",
        desc: "Vibrant, deep red chilli powder for Bengali food preparation.",
        options: ["Mild", "Medium", "Hot"],
      },
      {
        id: 10,
        name: "Mango Pickle",
        price: 249,
        img: "https://images.unsplash.com/photo-1599909533528-19bb5f0d4ad6?w=900&q=80",
        cat: "Bengal Food",
        tag: "Homemade",
        desc: "Traditional aam achar pickle prepared by rural women-led groups.",
        options: ["Mango", "Mixed", "Chilli"],
      },
      {
        id: 11,
        name: "Nolen Gur Jaggery",
        price: 299,
        img: "https://images.unsplash.com/photo-1601050690597-df0568f70950?w=900&q=80",
        cat: "Bengal Food",
        tag: "Bengal Winter",
        desc: "Bengal date-palm jaggery — the iconic winter sweetener for sandesh and payesh.",
        options: ["Block", "Powder", "Gift Jar"],
      },
      {
        id: 12,
        name: "Darjeeling Tea Gift Tin",
        price: 499,
        img: "https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=900&q=80",
        cat: "Bengal Food",
        tag: "Tea Heritage",
        desc: "Premium first-flush Darjeeling tea gifting tin from West Bengal's iconic hills.",
        options: ["Black", "Green", "Gift Tin"],
      },
      {
        id: 13,
        name: "Gobindobhog Rice Pack",
        price: 220,
        img: "https://images.unsplash.com/photo-1586201375761-83865001e31c?w=900&q=80",
        cat: "Bengal Food",
        tag: "Aromatic Rice",
        desc: "Short-grain aromatic rice used in Bengali festive pujas and payesh.",
        options: ["1kg", "2kg", "5kg"],
      },
    ],
  },

  {
    id: "handloom",
    label: "Bengal Sarees & Handloom",
    emoji: "🥻",
    accent: "#9DCD43",
    products: [
      {
        id: 14,
        name: "Tant Cotton Saree",
        price: 1299,
        img: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=900&q=80",
        cat: "Handloom",
        tag: "Bengal Classic",
        desc: "Lightweight handwoven cotton tant saree — Bengal's everyday elegance.",
        options: ["Daily Wear", "Festive Border", "Office Wear"],
      },
      {
        id: 15,
        name: "Baluchari Silk Saree",
        price: 3499,
        img: "https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=900&q=80",
        cat: "Handloom",
        tag: "Bishnupur Heritage",
        desc: "Heritage saree from Bishnupur with mythological storytelling motifs woven in silk.",
        options: ["Maroon", "Royal Blue", "Deep Green"],
      },
      {
        id: 16,
        name: "Jamdani Motif Saree",
        price: 2899,
        img: "https://images.unsplash.com/photo-1602573991155-21f0143bb45c?w=900&q=80",
        cat: "Handloom",
        tag: "Fine Motif",
        desc: "Fine supplementary weft motif saree — a UNESCO-recognised handloom tradition.",
        options: ["White", "Pastel", "Festive"],
      },
      {
        id: 17,
        name: "Garad Red Border Saree",
        price: 2199,
        img: "https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?w=900&q=80",
        cat: "Handloom",
        tag: "Puja Wear",
        desc: "Traditional off-white silk saree with vivid red border — Bengal puja staple.",
        options: ["Classic", "Temple Border", "Premium Silk"],
      },
      {
        id: 18,
        name: "Korial Silk Saree",
        price: 2999,
        img: "https://images.unsplash.com/photo-1609234053764-0e02a2cfab04?w=900&q=80",
        cat: "Handloom",
        tag: "Traditional Silk",
        desc: "Rich red-bordered silk saree — the defining identity of Bengal silk weaving.",
        options: ["Red Border", "Gold Border", "Festive"],
      },
      {
        id: 19,
        name: "Kantha Embroidery Saree",
        price: 2699,
        img: "https://images.unsplash.com/photo-1590735213920-68192a487bc2?w=900&q=80",
        cat: "Handloom",
        tag: "Hand Embroidered",
        desc: "Running stitch kantha embroidery on soft cotton — Bengal's most loved textile art.",
        options: ["Floral", "Folk Story", "Premium"],
      },
      {
        id: 20,
        name: "Murshidabad Silk Saree",
        price: 3199,
        img: "https://images.unsplash.com/photo-1634075080154-1e5e2e3c08f0?w=900&q=80",
        cat: "Handloom",
        tag: "Silk Craft",
        desc: "Lustrous Murshidabad silk with zari border — royalty-inspired weaving from Bengal.",
        options: ["Classic", "Zari Border", "Wedding"],
      },
      {
        id: 21,
        name: "Tribal Textile Stole",
        price: 1099,
        img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80",
        cat: "Handloom",
        tag: "Tribal Pattern",
        desc: "Handwoven tribal pattern stole from West Bengal's Santali and tribal weaving communities.",
        options: ["Earth Tone", "Red Black", "Green Border"],
      },
    ],
  },

  {
    id: "craft",
    label: "Handicrafts & Tribal Items",
    emoji: "🎨",
    accent: "#B4E065",
    products: [
      {
        id: 22,
        name: "Jute Tote Bag",
        price: 299,
        img: "https://images.unsplash.com/photo-1622560480605-d83c853bc5c3?w=900&q=80",
        cat: "Craft",
        tag: "Bengal Jute",
        desc: "Handcrafted reusable jute bag — Bengal's iconic natural fibre craft.",
        options: ["Plain", "Printed", "Custom Logo"],
      },
      {
        id: 23,
        name: "Bamboo Basket",
        price: 399,
        img: "https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?w=900&q=80",
        cat: "Craft",
        tag: "Eco Utility",
        desc: "Handwoven bamboo utility basket made by tribal artisans of West Bengal.",
        options: ["Small", "Medium", "Large"],
      },
      {
        id: 24,
        name: "Terracotta Horse Decor",
        price: 699,
        img: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=900&q=80",
        cat: "Craft",
        tag: "Bankura Inspired",
        desc: "Iconic Bankura-style terracotta horse — Bengal's most recognised folk art piece.",
        options: ["Small", "Medium", "Collector"],
      },
      {
        id: 25,
        name: "Clay Diya Set",
        price: 249,
        img: "https://images.unsplash.com/photo-1604608672516-5f252a6b9c0d?w=900&q=80",
        cat: "Craft",
        tag: "Festival Ready",
        desc: "Handmade clay diyas crafted by women artisans for puja and festivals.",
        options: ["Plain", "Painted", "Gift Box"],
      },
      {
        id: 26,
        name: "Dokra Inspired Figurine",
        price: 799,
        img: "https://images.unsplash.com/photo-1617788138017-80ad40651399?w=900&q=80",
        cat: "Craft",
        tag: "Tribal Art",
        desc: "Lost-wax cast brass figurine inspired by West Bengal's ancient Dokra tradition.",
        options: ["Small", "Medium", "Collector"],
      },
      {
        id: 27,
        name: "Wooden Folk Mask",
        price: 899,
        img: "https://images.unsplash.com/photo-1567359781514-81173b801d98?w=900&q=80",
        cat: "Craft",
        tag: "Cultural Decor",
        desc: "Hand-carved and painted folk mask inspired by Bengal's Chhau dance tradition.",
        options: ["Natural", "Painted", "Premium"],
      },
      {
        id: 28,
        name: "Kantha Cushion Cover",
        price: 449,
        img: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=900&q=80",
        cat: "Craft",
        tag: "Embroidery Craft",
        desc: "Kantha hand-stitched cushion cover — functional home textile with Bengal's story.",
        options: ["16 inch", "18 inch", "Set of 2"],
      },
      {
        id: 29,
        name: "Palm Leaf Craft Tray",
        price: 449,
        img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&q=80",
        cat: "Craft",
        tag: "Rural Utility",
        desc: "Naturally woven palm leaf serving tray — eco-craft from rural Bengal.",
        options: ["Round", "Square", "Serving Tray"],
      },
    ],
  },
];

const KEY_CONTRIBUTORS = [
  {
    name: "Adv. Anil Kodachadri",
    role: "Vice President, IS&SF",
    contribution: "Architect of the entrepreneurship participation framework — envisioning internal and external entrepreneurs with working and silent equity investor categories.",
    img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=300&q=80",
  },
  {
    name: "Kalamandalam Swarnadeepa",
    role: "Silk & Weaving Expert",
    contribution: "Expertise in silk sarees and weaving quality assessment, ensuring product authenticity and craftsmanship standards for the platform.",
    img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=300&q=80",
  },
  {
    name: "Mr. Vikas",
    role: "SHG & FSSAI Ecosystem Lead",
    contribution: "Grassroots SHG connections and FSSAI-linked food ecosystem network enabling regulatory-compliant food product integration.",
    img: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=300&q=80",
  },
  {
    name: "Ms. Anamika",
    role: "Food Processing Industry Link",
    contribution: "Food processing industry linkage potential, providing pathways for product diversification and market expansion.",
    img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=300&q=80",
  },
  {
    name: "Mr. Piyush",
    role: "E-Commerce & Digital Partner",
    contribution: "E-Commerce and digital marketplace partnership role, driving technology and online presence strategy.",
    img: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=300&q=80",
  },
  {
    name: "Devanjan Bose",
    role: "Founder Sewak, IS&SF",
    contribution: "Visionary behind the Nari Samman initiative — integrating rural empowerment, heritage revival and digital commerce.",
    img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=300&q=80",
  },
];

const REVENUE_STREAMS = [
  { label: "Product Sales Margins", icon: "💰", desc: "Direct margin from food, textile and craft product sales." },
  { label: "E-Commerce Commission", icon: "🛒", desc: "Platform commission from digital marketplace transactions." },
  { label: "Wholesale Supply Agreements", icon: "📦", desc: "Bulk supply contracts with retailers and institutional buyers." },
  { label: "Branding & Packaging Services", icon: "🏷️", desc: "Fee-based branding, labelling and packaging services for SHGs." },
  { label: "Export-Oriented Sales", icon: "🌏", desc: "Premium revenue from global ethnic and diaspora markets." },
  { label: "Artisan Membership", icon: "🎖️", desc: "Membership plans for artisans and SHG networks." },
];

const FUNDING_SOURCES = [
  {
    label: "Equity Contributions",
    color: "#9DCD43",
    items: ["Working entrepreneurs", "Silent investors", "Strategic partners"],
  },
  {
    label: "Institutional Finance",
    color: "#B4E065",
    items: ["Scheduled Commercial Banks", "Cooperative Banks", "SIDBI", "NABARD-linked financing"],
  },
  {
    label: "NABARD Support",
    color: "#9DCD43",
    items: ["Warehouse subsidy schemes", "Rural infrastructure refinance", "Agri-allied value chain", "Women SHG support"],
  },
  {
    label: "Government Schemes",
    color: "#B4E065",
    items: ["MSME schemes", "NRLM", "PMFME Scheme", "Handloom & Handicraft Schemes"],
  },
];

/* ───────────────────────────────────────────────
   DARK NAVY + LIME GREEN THEME
─────────────────────────────────────────────── */

const S = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@500;600;700;800&family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;0,900;1,400&display=swap');

*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html{scroll-behavior:smooth;}
body{font-family:'DM Sans',system-ui,sans-serif;background:#1C2437;color:#FFFFFF;min-height:100vh;overflow-x:hidden;}

.page{padding-top:66px;background:#1C2437;}

.nav{position:fixed;top:0;left:0;right:0;z-index:1000;background:rgba(19,29,41,0.96);backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);border-bottom:1px solid rgba(157,205,67,0.15);transition:all 0.3s ease;}
.nav.scrolled{box-shadow:0 10px 35px rgba(0,0,0,0.35);}
.nav-inner{max-width:1280px;margin:0 auto;padding:0 1.4rem;height:66px;display:flex;align-items:center;justify-content:space-between;gap:1rem;}
.nav-logo{font-family:'Syne',serif;font-size:1.45rem;font-weight:800;cursor:pointer;color:#9DCD43;white-space:nowrap;}
.nav-logo span{color:#FFFFFF;}
.hamburger{display:none;background:#9DCD43;color:#131D29;border:0;padding:8px 12px;border-radius:12px;cursor:pointer;font-size:1.1rem;}
.nav-links{display:flex;list-style:none;gap:0.2rem;overflow-x:auto;}
.nav-links::-webkit-scrollbar{display:none;}
.nav-links li button{background:transparent;border:0;cursor:pointer;padding:8px 11px;font-size:0.78rem;font-weight:800;color:#8A94A8;border-radius:999px;white-space:nowrap;transition:all 0.2s ease;font-family:'DM Sans',sans-serif;}
.nav-links li button:hover{color:#FFFFFF;background:rgba(157,205,67,0.12);}
.nav-links li button.active{background:linear-gradient(135deg,#7AAF2A,#9DCD43);color:#131D29;}
.nav-cart-btn{display:flex;align-items:center;gap:8px;background:linear-gradient(135deg,#9DCD43,#B4E065);border:0;color:#131D29;padding:10px 16px;border-radius:999px;cursor:pointer;font-size:0.82rem;font-weight:900;white-space:nowrap;box-shadow:0 10px 26px rgba(157,205,67,0.28);font-family:'DM Sans',sans-serif;}
.cart-badge{background:#131D29;color:#9DCD43;min-width:21px;height:21px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:0.72rem;font-weight:900;}

.sec,.sec-alt{padding:82px 1.5rem;background:#1C2437;}
.sec-alt{background:linear-gradient(180deg,#131D29 0%,#1C2437 100%);}
.sec-wrap{background:transparent;}
.container{max-width:1160px;margin:0 auto;}
.sec-tag{display:inline-flex;align-items:center;gap:8px;font-size:0.72rem;font-weight:900;letter-spacing:0.18em;text-transform:uppercase;color:#9DCD43;margin-bottom:0.75rem;font-family:'DM Sans',sans-serif;}
.sec-tag::before{content:'';width:28px;height:3px;background:linear-gradient(90deg,#7AAF2A,#9DCD43);border-radius:3px;}
.sec-h2{font-family:'Syne',serif;font-size:clamp(2rem,4.8vw,3.25rem);color:#FFFFFF;line-height:1.12;font-weight:800;margin-bottom:0.65rem;}
.sec-h2 em{color:#9DCD43;font-style:italic;}
.sec-div{width:58px;height:4px;background:linear-gradient(90deg,#7AAF2A,#9DCD43);border-radius:3px;margin:1rem 0;}
.sec-desc{font-size:1rem;color:#8A94A8;max-width:680px;line-height:1.85;}

.hero{min-height:100vh;position:relative;overflow:hidden;display:flex;flex-direction:column;background:radial-gradient(circle at 10% 18%,rgba(157,205,67,0.22),transparent 28%),radial-gradient(circle at 88% 12%,rgba(180,224,101,0.12),transparent 24%),linear-gradient(135deg,#0F1822 0%,#131D29 42%,#1C2437 78%,#243050 125%);color:#FFFFFF;}
.hero-bg{position:absolute;inset:0;background:linear-gradient(rgba(0,0,0,0.22),rgba(0,0,0,0.30)),url('https://images.unsplash.com/photo-1590502593747-42a996133562?auto=format&fit=crop&w=1800&q=80') center/cover no-repeat;opacity:0.12;filter:saturate(0.5);}
.hero-overlay{position:absolute;inset:0;background:linear-gradient(135deg,rgba(15,24,34,0.97),rgba(19,29,41,0.90),rgba(157,205,67,0.12));}
.hero-grid-bg{position:absolute;inset:0;opacity:0.06;background-image:linear-gradient(rgba(157,205,67,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(157,205,67,0.5) 1px,transparent 1px);background-size:52px 52px;}
.hero-content{position:relative;z-index:2;flex:1;display:flex;align-items:center;padding:110px 1.5rem 70px;}
.hero-inner{max-width:1160px;margin:0 auto;width:100%;}
.hero-layout{display:grid;grid-template-columns:1.08fr 0.92fr;gap:4rem;align-items:center;}
.hero-badge{display:inline-flex;align-items:center;gap:8px;background:rgba(157,205,67,0.12);border:1px solid rgba(157,205,67,0.28);color:#9DCD43;font-size:0.72rem;font-weight:900;letter-spacing:0.17em;text-transform:uppercase;padding:8px 17px;border-radius:999px;margin-bottom:2rem;font-family:'DM Sans',sans-serif;}
.hero-badge span{width:7px;height:7px;border-radius:50%;background:#9DCD43;display:inline-block;}
.hero-h1{font-family:'Syne',serif;font-size:clamp(3.8rem,9.5vw,7.4rem);line-height:0.92;font-weight:800;margin-bottom:1.4rem;text-shadow:0 18px 50px rgba(0,0,0,0.40);color:#FFFFFF;}
.hero-h1 em{display:block;font-style:italic;color:#9DCD43;}
.hero-sub{font-size:1.05rem;color:rgba(255,255,255,0.72);line-height:1.9;max-width:520px;margin-bottom:1.4rem;}
.hero-quote{font-family:'Syne',serif;font-style:italic;font-size:1.08rem;color:rgba(157,205,67,0.70);margin-bottom:2.4rem;}
.hero-cta-row{display:flex;gap:1rem;flex-wrap:wrap;}
.btn-gold{background:linear-gradient(135deg,#9DCD43,#B4E065);color:#131D29;border:0;padding:15px 32px;border-radius:999px;font-size:0.94rem;font-weight:900;cursor:pointer;box-shadow:0 15px 42px rgba(157,205,67,0.28);font-family:'DM Sans',sans-serif;}
.btn-glass{background:rgba(157,205,67,0.10);color:#FFFFFF;border:1.5px solid rgba(157,205,67,0.30);padding:14px 30px;border-radius:999px;font-size:0.94rem;font-weight:900;cursor:pointer;backdrop-filter:blur(8px);font-family:'DM Sans',sans-serif;}
.hero-stats{display:grid;grid-template-columns:1fr 1fr;gap:1.25rem;}
.hero-stat{background:rgba(157,205,67,0.08);border:1px solid rgba(157,205,67,0.18);border-radius:22px;padding:1.8rem 1.5rem;backdrop-filter:blur(12px);box-shadow:0 20px 50px rgba(0,0,0,0.22);}
.hero-stat-n{font-family:'Syne',serif;font-size:2.9rem;font-weight:800;color:#9DCD43;line-height:1;}
.hero-stat-l{font-size:0.85rem;color:rgba(255,255,255,0.62);margin-top:7px;line-height:1.45;}
.hero-strip{position:relative;z-index:2;display:flex;background:rgba(19,29,41,0.85);backdrop-filter:blur(12px);border-top:1px solid rgba(157,205,67,0.14);}
.hero-strip-item{flex:1;padding:20px 22px;display:flex;align-items:center;gap:12px;border-right:1px solid rgba(157,205,67,0.10);}
.hero-strip-item:last-child{border-right:none;}
.strip-icon{font-size:1.45rem;}
.strip-text{font-size:0.8rem;color:rgba(255,255,255,0.58);line-height:1.45;}
.strip-text strong{display:block;color:#FFFFFF;font-size:0.88rem;}

.glass,.glass-dark,.glass-card{background:#131D29;border:1px solid rgba(157,205,67,0.14);border-radius:22px;box-shadow:0 18px 55px rgba(0,0,0,0.25);}
.glass-card{transition:transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;}
.glass-card:hover{transform:translateY(-4px);box-shadow:0 24px 70px rgba(0,0,0,0.35);border-color:rgba(157,205,67,0.32);}

.vision-q{font-family:'Syne',serif;font-size:1.2rem;font-style:italic;color:#FFFFFF;line-height:1.8;padding:2rem 2rem 2rem 2.4rem;border-left:5px solid #9DCD43;background:linear-gradient(135deg,rgba(157,205,67,0.08),rgba(19,29,41,0.60));border-radius:0 22px 22px 0;margin-bottom:2.5rem;}

.box-green{background:linear-gradient(135deg,#131D29,#1C2437);color:#FFFFFF;border:1px solid rgba(157,205,67,0.20);border-radius:24px;padding:2.5rem;box-shadow:0 22px 60px rgba(0,0,0,0.28);}
.box-green h3{font-family:'Syne',serif;font-size:1.35rem;color:#9DCD43;margin-bottom:1rem;}
.box-green p{color:#8A94A8;font-size:0.95rem;line-height:1.8;}
.box-orange{background:linear-gradient(135deg,#9DCD43,#B4E065);color:#131D29;border-radius:24px;padding:2.5rem;box-shadow:0 22px 60px rgba(157,205,67,0.22);}
.box-orange h3{font-family:'Syne',serif;font-size:1.35rem;color:#131D29;margin-bottom:1rem;}
.box-orange p{color:rgba(19,29,41,0.82);font-size:0.95rem;line-height:1.8;}

.mission-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(270px,1fr));gap:1rem;}
.mission-item{display:flex;align-items:flex-start;gap:1rem;padding:1.25rem 1.5rem;background:#131D29;border:1px solid rgba(157,205,67,0.12);border-radius:18px;box-shadow:0 14px 40px rgba(0,0,0,0.20);transition:all 0.22s ease;}
.mission-item:hover{transform:translateY(-3px);border-color:rgba(157,205,67,0.32);}
.mission-n{min-width:36px;height:36px;border-radius:50%;background:linear-gradient(135deg,#7AAF2A,#9DCD43);color:#131D29;font-weight:900;font-size:0.85rem;display:flex;align-items:center;justify-content:center;}
.mission-t{font-size:0.9rem;color:#8A94A8;line-height:1.65;padding-top:4px;}

.flist{list-style:none;}
.flist li{display:flex;align-items:flex-start;gap:10px;padding:0.65rem 0;border-bottom:1px solid rgba(157,205,67,0.10);font-size:0.9rem;color:#8A94A8;line-height:1.65;}
.flist li:last-child{border-bottom:none;}
.flist li::before{content:'✦';color:#9DCD43;flex-shrink:0;font-size:0.72rem;margin-top:6px;}

.pilot-chips{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:0.75rem;margin-top:2rem;}
.pilot-chip{background:#131D29;border:1px solid rgba(157,205,67,0.14);border-radius:14px;padding:0.9rem 1.1rem;font-size:0.88rem;color:#C8D0E4;font-weight:700;display:flex;align-items:center;gap:8px;box-shadow:0 12px 34px rgba(0,0,0,0.18);}
.pilot-chip::before{content:'◆';color:#9DCD43;font-size:0.55rem;}

.prod-tabs{display:flex;gap:0.65rem;flex-wrap:wrap;margin-bottom:2rem;}
.prod-tab{padding:10px 18px;border-radius:999px;background:#131D29;border:1px solid rgba(157,205,67,0.16);cursor:pointer;font-size:0.84rem;font-weight:900;color:#8A94A8;transition:all 0.2s ease;box-shadow:0 8px 26px rgba(0,0,0,0.18);font-family:'DM Sans',sans-serif;}
.prod-tab:hover{color:#FFFFFF;border-color:rgba(157,205,67,0.40);}
.prod-tab.active{background:linear-gradient(135deg,#7AAF2A,#9DCD43);border-color:transparent;color:#131D29;}
.prod-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:1.35rem;align-items:stretch;}
.prod-card{background:#131D29;border:1px solid rgba(157,205,67,0.12);border-radius:24px;overflow:hidden;cursor:pointer;box-shadow:0 18px 50px rgba(0,0,0,0.25);transition:transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;display:flex;flex-direction:column;min-height:100%;}
.prod-card:hover{transform:translateY(-5px);box-shadow:0 26px 70px rgba(0,0,0,0.35);border-color:rgba(157,205,67,0.32);}
.prod-img-wrap{overflow:hidden;height:205px;position:relative;background:#0F1822;flex:0 0 205px;}
.prod-img-wrap img{width:100%;height:100%;object-fit:cover;display:block;transition:transform 0.45s ease;}
.prod-card:hover .prod-img-wrap img{transform:scale(1.07);}
.prod-img-wrap .prod-badge{position:absolute;top:12px;right:12px;background:rgba(19,29,41,0.92);color:#9DCD43;font-size:0.68rem;font-weight:900;padding:5px 11px;border-radius:999px;box-shadow:0 8px 18px rgba(0,0,0,0.30);}
.prod-body{padding:1.1rem 1.15rem 1.25rem;display:grid;grid-template-rows:auto auto minmax(44px,1fr) auto;gap:0.45rem;height:100%;}
.prod-name{font-family:'Syne',serif;font-weight:800;font-size:1.05rem;color:#FFFFFF;min-height:48px;line-height:1.25;}
.prod-cat{font-size:0.74rem;color:#9DCD43;font-weight:900;text-transform:uppercase;letter-spacing:0.06em;}
.prod-desc{font-size:0.82rem;color:#8A94A8;line-height:1.55;}
.prod-option{width:100%;padding:9px 11px;border-radius:12px;border:1px solid rgba(157,205,67,0.18);background:#1C2437;color:#FFFFFF;font-size:0.82rem;font-weight:700;outline:none;font-family:'DM Sans',sans-serif;}
.prod-row{display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:0.5rem;}
.prod-price{font-family:'Syne',serif;font-size:1.2rem;font-weight:900;color:#9DCD43;}
.add-cart-btn{background:linear-gradient(135deg,#9DCD43,#B4E065);border:0;color:#131D29;padding:8px 15px;border-radius:999px;font-size:0.76rem;font-weight:900;cursor:pointer;white-space:nowrap;transition:all 0.2s ease;font-family:'DM Sans',sans-serif;}
.add-cart-btn:hover{transform:translateY(-1px);box-shadow:0 10px 24px rgba(157,205,67,0.28);}
.add-cart-btn.added{background:linear-gradient(135deg,#7AAF2A,#9DCD43);}

.cart-overlay{position:fixed;inset:0;background:rgba(15,24,34,0.70);z-index:2000;backdrop-filter:blur(4px);display:flex;justify-content:flex-end;}
.cart-drawer{width:min(440px,100vw);background:#131D29;border-left:1px solid rgba(157,205,67,0.14);display:flex;flex-direction:column;height:100vh;overflow:hidden;animation:slideIn 0.3s ease;box-shadow:-22px 0 70px rgba(0,0,0,0.45);}
@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}
.cart-head{padding:1.5rem;border-bottom:1px solid rgba(157,205,67,0.10);display:flex;align-items:center;justify-content:space-between;}
.cart-title{font-family:'Syne',serif;font-size:1.25rem;color:#FFFFFF;font-weight:800;}
.cart-close{background:#1C2437;border:0;color:#FFFFFF;width:35px;height:35px;border-radius:50%;cursor:pointer;font-size:1.1rem;display:flex;align-items:center;justify-content:center;}
.cart-items{flex:1;overflow-y:auto;padding:1rem 1.5rem;}
.cart-item{display:flex;gap:12px;padding:1rem 0;border-bottom:1px solid rgba(157,205,67,0.08);}
.cart-item img{width:62px;height:62px;object-fit:cover;border-radius:12px;}
.cart-item-info{flex:1;min-width:0;}
.cart-item-name{font-size:0.9rem;font-weight:900;color:#FFFFFF;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.cart-item-cat{font-size:0.74rem;color:#9DCD43;font-weight:800;margin:2px 0 8px;}
.cart-qty-row{display:flex;align-items:center;gap:8px;}
.qty-btn{width:27px;height:27px;border-radius:50%;background:#1C2437;border:0;color:#FFFFFF;font-weight:900;cursor:pointer;}
.qty-val{font-size:0.9rem;font-weight:900;color:#FFFFFF;min-width:20px;text-align:center;}
.cart-item-price{font-family:'Syne',serif;font-size:1rem;color:#9DCD43;font-weight:900;white-space:nowrap;align-self:center;}
.cart-rm{background:transparent;border:0;color:#E74C3C;cursor:pointer;font-size:0.85rem;}
.cart-footer{padding:1.5rem;border-top:1px solid rgba(157,205,67,0.10);}
.cart-total-row{display:flex;justify-content:space-between;margin-bottom:1rem;}
.cart-total-label{font-size:0.95rem;color:#8A94A8;font-weight:700;}
.cart-total-val{font-family:'Syne',serif;font-size:1.35rem;color:#9DCD43;font-weight:900;}
.cart-checkout-btn{width:100%;background:linear-gradient(135deg,#7AAF2A,#9DCD43);color:#131D29;border:0;padding:15px;border-radius:999px;font-size:1rem;font-weight:900;cursor:pointer;font-family:'DM Sans',sans-serif;}
.cart-empty{text-align:center;padding:4rem 2rem;color:#8A94A8;}
.cart-empty .empty-icon{font-size:4rem;margin-bottom:1rem;}

.modal-overlay{position:fixed;inset:0;background:rgba(15,24,34,0.75);z-index:3000;display:flex;align-items:center;justify-content:center;padding:1rem;backdrop-filter:blur(7px);}
.modal{background:#131D29;border:1px solid rgba(157,205,67,0.14);border-radius:26px;width:100%;max-width:540px;max-height:90vh;overflow-y:auto;box-shadow:0 28px 90px rgba(0,0,0,0.55);}
.modal-head{padding:2rem 2rem 1rem;border-bottom:1px solid rgba(157,205,67,0.10);display:flex;align-items:center;justify-content:space-between;}
.modal-title{font-family:'Syne',serif;font-size:1.35rem;color:#FFFFFF;font-weight:900;}
.modal-body{padding:2rem;}
.payment-steps{display:flex;gap:0;margin-bottom:2.5rem;border-radius:999px;overflow:hidden;border:1px solid rgba(157,205,67,0.14);}
.pay-step{flex:1;padding:10px;text-align:center;font-size:0.76rem;font-weight:900;color:#8A94A8;background:#1C2437;font-family:'DM Sans',sans-serif;}
.pay-step.active{background:linear-gradient(135deg,#7AAF2A,#9DCD43);color:#131D29;}
.pay-step.done{background:rgba(157,205,67,0.15);color:#9DCD43;}
.form-g{display:flex;flex-direction:column;gap:6px;margin-bottom:1.25rem;}
.form-l{font-size:0.76rem;font-weight:900;color:#8A94A8;letter-spacing:0.08em;text-transform:uppercase;font-family:'DM Sans',sans-serif;}
.form-i{background:#1C2437;border:1.5px solid rgba(157,205,67,0.16);border-radius:14px;padding:13px 16px;font-size:0.94rem;color:#FFFFFF;outline:none;width:100%;font-family:'DM Sans',sans-serif;}
.form-i:focus{border-color:#9DCD43;box-shadow:0 0 0 4px rgba(157,205,67,0.10);}
.form-i.err{border-color:#E74C3C;}
.form-i::placeholder{color:#8A94A8;}
.form-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem;}
.err-msg{font-size:0.74rem;color:#E74C3C;}
.pay-methods{display:grid;grid-template-columns:1fr 1fr 1fr;gap:0.75rem;margin-bottom:1.5rem;}
.pay-method{padding:1rem;border-radius:16px;border:1.5px solid rgba(157,205,67,0.14);background:#1C2437;cursor:pointer;text-align:center;transition:all 0.2s ease;}
.pay-method.sel{border-color:#9DCD43;background:rgba(157,205,67,0.10);}
.pay-method-icon{font-size:1.8rem;display:block;margin-bottom:6px;}
.pay-method-label{font-size:0.78rem;font-weight:900;color:#FFFFFF;font-family:'DM Sans',sans-serif;}
.cod-info{background:rgba(157,205,67,0.07);border:1px solid rgba(157,205,67,0.18);border-radius:16px;padding:1.25rem;margin-bottom:1.5rem;display:flex;gap:14px;align-items:flex-start;}
.cod-info-icon{font-size:2rem;flex-shrink:0;}
.cod-info-title{font-size:0.92rem;font-weight:900;color:#9DCD43;margin-bottom:4px;}
.cod-info-desc{font-size:0.82rem;color:#8A94A8;line-height:1.6;}
.order-sum{background:#1C2437;border:1px solid rgba(157,205,67,0.10);border-radius:16px;padding:1.25rem;margin-bottom:1.5rem;}
.order-sum-row{display:flex;justify-content:space-between;margin-bottom:8px;font-size:0.9rem;color:#8A94A8;}
.order-sum-row:last-child{margin-bottom:0;padding-top:10px;border-top:1px solid rgba(157,205,67,0.10);font-weight:900;color:#FFFFFF;font-size:1rem;}
.order-sum-row span:last-child{color:#9DCD43;font-weight:900;}
.modal-btn{width:100%;background:linear-gradient(135deg,#7AAF2A,#9DCD43);color:#131D29;border:0;padding:15px;border-radius:999px;font-size:1rem;font-weight:900;cursor:pointer;font-family:'DM Sans',sans-serif;}
.modal-btn-back{width:100%;background:#1C2437;color:#FFFFFF;border:1px solid rgba(157,205,67,0.16);padding:13px;border-radius:999px;font-size:0.9rem;font-weight:900;cursor:pointer;margin-top:0.75rem;font-family:'DM Sans',sans-serif;}
.success-wrap{text-align:center;padding:2rem;}
.success-icon{font-size:5rem;display:block;margin:0 auto 1.5rem;}
.success-h{font-family:'Syne',serif;font-size:1.7rem;color:#9DCD43;margin-bottom:0.75rem;}
.success-p{color:#8A94A8;font-size:0.95rem;line-height:1.75;margin-bottom:1.5rem;}
.order-id{background:rgba(157,205,67,0.10);border:1px solid rgba(157,205,67,0.22);border-radius:14px;padding:1rem;color:#9DCD43;font-size:1.1rem;font-weight:900;}

.org-top{background:linear-gradient(135deg,#131D29,#1C2437);border:1px solid rgba(157,205,67,0.20);border-radius:18px;padding:1.35rem 2rem;text-align:center;font-family:'Syne',serif;font-size:1.1rem;font-weight:900;color:#9DCD43;margin-bottom:1rem;}
.org-chips{display:flex;flex-wrap:wrap;gap:0.7rem;justify-content:center;margin-bottom:3rem;}
.org-chip{background:#131D29;border:1px solid rgba(157,205,67,0.16);border-radius:999px;padding:9px 18px;font-size:0.84rem;color:#C8D0E4;font-weight:800;box-shadow:0 10px 26px rgba(0,0,0,0.20);}
.entrepn-grid{display:grid;grid-template-columns:1fr 1fr;gap:2rem;}
.entrepn-card{border-radius:24px;padding:2rem;border:1px solid rgba(157,205,67,0.14);background:#131D29;box-shadow:0 18px 50px rgba(0,0,0,0.25);}
.ebadge{display:inline-block;font-size:0.7rem;letter-spacing:0.12em;text-transform:uppercase;padding:5px 14px;border-radius:999px;margin-bottom:1rem;font-weight:900;font-family:'DM Sans',sans-serif;}
.ebadge-g{background:rgba(157,205,67,0.12);color:#9DCD43;}
.ebadge-o{background:rgba(180,224,101,0.12);color:#B4E065;}
.epct{font-family:'Syne',serif;font-size:4rem;font-weight:900;line-height:1;margin-bottom:0.5rem;}

.section-img{width:100%;height:260px;object-fit:cover;border-radius:24px;margin-bottom:2rem;box-shadow:0 20px 55px rgba(0,0,0,0.35);}
.two-col{display:grid;grid-template-columns:1fr 1fr;gap:3rem;}
.channel-row{display:flex;align-items:center;gap:14px;background:#131D29;border:1px solid rgba(157,205,67,0.12);border-radius:16px;padding:1rem 1.25rem;margin-bottom:0.7rem;box-shadow:0 12px 34px rgba(0,0,0,0.18);}
.channel-icon{font-size:1.45rem;width:38px;text-align:center;}
.channel-l{font-size:0.9rem;color:#C8D0E4;font-weight:800;}
.ch-num{margin-left:auto;min-width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,#7AAF2A,#9DCD43);color:#131D29;font-size:0.74rem;font-weight:900;display:flex;align-items:center;justify-content:center;}

.weave-img-grid{display:grid;grid-template-columns:2fr 1fr 1fr;gap:1rem;margin-bottom:2.5rem;border-radius:24px;overflow:hidden;}
.weave-img-grid img{width:100%;object-fit:cover;display:block;}
.weave-img-grid img:first-child{height:280px;}
.weave-img-grid img:not(:first-child){height:136px;}
.weave-stat-row{display:grid;grid-template-columns:repeat(3,1fr);gap:1.5rem;margin-bottom:3rem;}
.weave-s{text-align:center;padding:2rem 1rem;background:#131D29;border:1px solid rgba(157,205,67,0.12);border-radius:24px;box-shadow:0 16px 45px rgba(0,0,0,0.22);}
.weave-s .big{font-family:'Syne',serif;font-size:3.2rem;font-weight:900;color:#9DCD43;line-height:1;}
.weave-s .lbl{font-size:0.82rem;color:#8A94A8;margin-top:0.6rem;line-height:1.5;}
.focus-item{display:flex;align-items:center;gap:14px;padding:0.9rem 1.25rem;background:rgba(157,205,67,0.06);border:1px solid rgba(157,205,67,0.14);border-radius:16px;margin-bottom:0.7rem;}
.focus-n{font-family:'Syne',serif;font-weight:900;font-size:1.15rem;color:#9DCD43;min-width:24px;}
.focus-l{font-size:0.9rem;color:#C8D0E4;font-weight:800;}

.fin-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:1.5rem;}
.fin-card{border-radius:24px;padding:1.75rem;border:1px solid rgba(157,205,67,0.12);background:#131D29;box-shadow:0 16px 45px rgba(0,0,0,0.22);}
.fin-head{font-size:0.72rem;text-transform:uppercase;letter-spacing:0.14em;font-weight:900;margin-bottom:1.25rem;padding-bottom:0.75rem;border-bottom:2px solid currentColor;font-family:'DM Sans',sans-serif;}

.rev-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:1.25rem;}
.rev-card{display:flex;gap:1.25rem;align-items:flex-start;background:#131D29;border:1px solid rgba(157,205,67,0.12);border-radius:24px;padding:1.5rem;box-shadow:0 16px 45px rgba(0,0,0,0.22);}
.rev-icon{width:54px;height:54px;border-radius:16px;background:linear-gradient(135deg,rgba(157,205,67,0.10),rgba(180,224,101,0.14));display:flex;align-items:center;justify-content:center;font-size:1.5rem;flex-shrink:0;}
.rev-num{font-family:'Syne',serif;font-size:1.4rem;font-weight:900;color:#9DCD43;line-height:1;margin-bottom:4px;}
.rev-label{font-size:0.95rem;font-weight:900;color:#FFFFFF;margin-bottom:4px;}
.rev-desc{font-size:0.82rem;color:#8A94A8;line-height:1.55;}

.team-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(310px,1fr));gap:1.5rem;}
.team-card{background:#131D29;border:1px solid rgba(157,205,67,0.12);border-radius:24px;padding:1.75rem;display:flex;gap:1.25rem;align-items:flex-start;box-shadow:0 16px 45px rgba(0,0,0,0.22);transition:all 0.22s ease;}
.team-card:hover{transform:translateY(-3px);border-color:rgba(157,205,67,0.30);}
.team-av{width:64px;height:64px;border-radius:50%;object-fit:cover;flex-shrink:0;border:3px solid rgba(157,205,67,0.28);}
.team-name{font-family:'Syne',serif;font-size:1.05rem;font-weight:900;color:#FFFFFF;}
.team-role{font-size:0.74rem;color:#9DCD43;font-weight:900;letter-spacing:0.07em;text-transform:uppercase;margin:5px 0 10px;}
.team-txt{font-size:0.86rem;color:#8A94A8;line-height:1.65;}

.cta-sec{padding:82px 1.5rem;position:relative;background:radial-gradient(circle at 10% 10%,rgba(157,205,67,0.12),transparent 30%),linear-gradient(135deg,#0F1822,#131D29,#1C2437);border-top:1px solid rgba(157,205,67,0.14);}
.cta-inner{max-width:700px;margin:0 auto;text-align:center;}
.cta-inner h2{font-family:'Syne',serif;font-size:clamp(2rem,4vw,3.1rem);color:#FFFFFF;margin-bottom:1rem;font-weight:900;}
.cta-inner p{color:#8A94A8;margin-bottom:2.5rem;font-size:1rem;line-height:1.8;}
.cta-form{display:flex;flex-direction:column;gap:0;text-align:left;}
.cform-row{display:grid;grid-template-columns:1fr 1fr;gap:1rem;margin-bottom:1.25rem;}
select.form-i option{background:#131D29;color:#FFFFFF;}
.form-submit{width:100%;background:linear-gradient(135deg,#9DCD43,#B4E065);color:#131D29;border:0;padding:15px;border-radius:999px;font-size:1rem;font-weight:900;cursor:pointer;margin-top:0.5rem;box-shadow:0 14px 34px rgba(157,205,67,0.22);font-family:'DM Sans',sans-serif;}
.success-banner{text-align:center;padding:2.5rem;background:#131D29;border:1px solid rgba(157,205,67,0.18);border-radius:24px;box-shadow:0 22px 60px rgba(0,0,0,0.35);}
.success-banner h3{font-family:'Syne',serif;color:#9DCD43;font-size:1.45rem;margin-bottom:0.5rem;}
.success-banner p{color:#8A94A8;}

footer{background:#0F1822;color:rgba(255,255,255,0.62);padding:4rem 1.5rem 2rem;border-top:1px solid rgba(157,205,67,0.10);}
.foot-grid{max-width:1160px;margin:0 auto;display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:3rem;margin-bottom:3rem;}
.foot-logo{font-family:'Syne',serif;font-size:1.9rem;font-weight:900;color:#9DCD43;margin-bottom:0.5rem;}
.foot-tag{font-style:italic;font-size:0.86rem;color:rgba(255,255,255,0.45);margin-bottom:1rem;}
.foot-desc{font-size:0.84rem;line-height:1.8;color:rgba(255,255,255,0.42);}
.foot-col h4{font-size:0.72rem;text-transform:uppercase;letter-spacing:0.15em;font-weight:900;color:#9DCD43;margin-bottom:1rem;font-family:'DM Sans',sans-serif;}
.foot-col ul{list-style:none;}
.foot-col li{margin-bottom:0.65rem;}
.foot-col li button{background:transparent;border:0;cursor:pointer;font-size:0.84rem;color:rgba(255,255,255,0.50);padding:0;text-align:left;font-family:'DM Sans',sans-serif;}
.foot-col li button:hover{color:#9DCD43;}
.foot-bottom{max-width:1160px;margin:0 auto;padding-top:2rem;border-top:1px solid rgba(157,205,67,0.10);display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:1rem;font-size:0.8rem;color:rgba(255,255,255,0.38);}

.sum-cards{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:1.5rem;}
.sum-card{padding:1.8rem;position:relative;overflow:hidden;}
.sum-card::before{content:'';position:absolute;top:0;left:0;right:0;height:4px;background:linear-gradient(90deg,#7AAF2A,#9DCD43);}
.sum-card-icon{font-size:2.2rem;margin-bottom:1rem;}
.sum-card-title{font-family:'Syne',serif;font-size:1.15rem;font-weight:900;color:#FFFFFF;margin-bottom:0.5rem;}
.sum-card-body{font-size:0.88rem;color:#8A94A8;line-height:1.7;}

.fade-in{opacity:0;transform:translateY(22px);transition:opacity 0.6s ease, transform 0.6s ease;}
.fade-in.visible{opacity:1;transform:translateY(0);}

.flow-item{display:flex;align-items:center;gap:1.25rem;background:#131D29;border:1px solid rgba(157,205,67,0.12);border-radius:18px;padding:1.1rem 1.5rem;margin-bottom:0.85rem;box-shadow:0 12px 34px rgba(0,0,0,0.18);}
.flow-n{min-width:38px;height:38px;border-radius:50%;background:linear-gradient(135deg,#7AAF2A,#9DCD43);color:#131D29;font-weight:900;font-size:0.96rem;display:flex;align-items:center;justify-content:center;}
.flow-t{font-size:0.9rem;color:#8A94A8;line-height:1.55;}
.flow-t strong{display:block;color:#FFFFFF;font-weight:900;margin-bottom:2px;}

.active-dot{display:inline-block;width:7px;height:7px;border-radius:50%;background:#9DCD43;margin-right:6px;vertical-align:middle;}
.sec-wrap{background:transparent;}

@media(max-width:960px){
  .hamburger{display:flex;align-items:center;}
  .nav-links{display:none;position:fixed;top:66px;left:0;right:0;bottom:0;background:#131D29;flex-direction:column;overflow-y:auto;padding:1rem 0;border-top:1px solid rgba(157,205,67,0.10);box-shadow:0 20px 60px rgba(0,0,0,0.45);}
  .nav-links.open{display:flex;}
  .nav-links li button{font-size:1rem;padding:14px 2rem;width:100%;text-align:left;border-radius:0;}
  .nav-links li button.active{background:linear-gradient(135deg,#7AAF2A,#9DCD43);color:#131D29;}
}
@media(max-width:768px){
  .hero-layout,.two-col{grid-template-columns:1fr;gap:2rem;}
  .hero-strip{flex-direction:column;}
  .hero-strip-item{border-right:none;border-bottom:1px solid rgba(157,205,67,0.10);}
  .weave-img-grid{grid-template-columns:1fr;}
  .weave-img-grid img{height:190px !important;}
  .weave-stat-row{grid-template-columns:1fr;}
  .foot-grid{grid-template-columns:1fr 1fr;gap:2rem;}
  .pay-methods{grid-template-columns:1fr;}
}
@media(max-width:600px){
  .sec,.sec-alt,.cta-sec{padding:64px 1rem;}
  .hero-content{padding:96px 1rem 56px;}
  .hero-stats{grid-template-columns:1fr;}
  .entrepn-grid,.form-row,.cform-row{grid-template-columns:1fr;}
  .prod-grid{grid-template-columns:1fr;}
  .team-grid{grid-template-columns:1fr;}
  .team-card{flex-direction:column;}
  .foot-grid{grid-template-columns:1fr;}
  .foot-bottom{flex-direction:column;align-items:flex-start;}
}
`;

/* ───────────────────────────────────────────────
   HELPERS
─────────────────────────────────────────────── */

function FadeIn({ children, id }) {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add("visible"); obs.disconnect(); } },
      { threshold: 0.07 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className="fade-in" id={id}>{children}</div>;
}

function SH({ tag, title, desc }) {
  return (
    <div style={{ marginBottom: "3rem" }}>
      <div className="sec-tag">{tag}</div>
      <h2 className="sec-h2" dangerouslySetInnerHTML={{ __html: title }} />
      <div className="sec-div" />
      {desc && <p className="sec-desc">{desc}</p>}
    </div>
  );
}

function FList({ items }) {
  return (
    <ul className="flist">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

/* ───────────────────────────────────────────────
   CART DRAWER
─────────────────────────────────────────────── */

function CartDrawer({ cart, setCart, onClose, onCheckout }) {
  const total = cart.reduce((sum, item) => sum + item.price * item.qty, 0);

  const updateQty = (id, change) => {
    setCart((items) =>
      items.map((item) =>
        item.id === id ? { ...item, qty: Math.max(1, item.qty + change) } : item
      )
    );
  };

  const removeItem = (id) => {
    setCart((items) => items.filter((item) => item.id !== id));
  };

  return (
    <div className="cart-overlay" onClick={onClose}>
      <div className="cart-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="cart-head">
          <div className="cart-title">
            🛒 Your Cart ({cart.reduce((sum, item) => sum + item.qty, 0)} items)
          </div>
          <button className="cart-close" onClick={onClose}>✕</button>
        </div>

        <div className="cart-items">
          {cart.length === 0 ? (
            <div className="cart-empty">
              <div className="empty-icon">🛍️</div>
              <p>Your cart is empty</p>
              <p style={{ fontSize: "0.82rem", marginTop: "0.5rem" }}>Add some products to get started.</p>
            </div>
          ) : (
            cart.map((item) => (
              <div className="cart-item" key={item.uniqueId || item.id}>
                <img src={item.img} alt={item.name} />
                <div className="cart-item-info">
                  <div className="cart-item-name">{item.name}</div>
                  <div className="cart-item-cat">{item.cat}{item.selectedOption ? ` · ${item.selectedOption}` : ""}</div>
                  <div className="cart-qty-row">
                    <button className="qty-btn" onClick={() => updateQty(item.uniqueId || item.id, -1)}>−</button>
                    <span className="qty-val">{item.qty}</span>
                    <button className="qty-btn" onClick={() => updateQty(item.uniqueId || item.id, 1)}>+</button>
                    <button className="cart-rm" onClick={() => removeItem(item.uniqueId || item.id)}>🗑️</button>
                  </div>
                </div>
                <div className="cart-item-price">₹{(item.price * item.qty).toLocaleString()}</div>
              </div>
            ))
          )}
        </div>

        {cart.length > 0 && (
          <div className="cart-footer">
            <div className="cart-total-row">
              <span className="cart-total-label">Total</span>
              <span className="cart-total-val">₹{total.toLocaleString()}</span>
            </div>
            <button className="cart-checkout-btn" onClick={onCheckout}>Proceed to Checkout →</button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────
   PAYMENT MODAL — with COD option
─────────────────────────────────────────────── */

function PaymentModal({ cart, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [method, setMethod] = useState("upi");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", city: "", pin: "",
    upi: "", card: "", expiry: "", cvv: "",
  });
  const [errors, setErrors] = useState({});

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const delivery = subtotal > 999 ? 0 : 49;
  const total = subtotal + delivery;
  const orderId = "NS" + Date.now().toString().slice(-8);

  const updateForm = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const validateDelivery = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email is required";
    if (!form.phone.trim() || form.phone.length < 10) e.phone = "Valid phone number is required";
    if (!form.address.trim()) e.address = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!form.pin.trim() || form.pin.length < 6) e.pin = "6-digit PIN code is required";
    return e;
  };

  const validatePayment = () => {
    const e = {};
    if (method === "upi") {
      if (!form.upi.trim()) e.upi = "UPI ID is required";
    }
    if (method === "card") {
      if (!form.card.trim()) e.card = "Card number is required";
      if (!form.expiry.trim()) e.expiry = "Expiry is required";
      if (!form.cvv.trim()) e.cvv = "CVV is required";
    }
    // COD needs no extra validation
    return e;
  };

  const nextDelivery = () => {
    const e = validateDelivery();
    setErrors(e);
    if (Object.keys(e).length === 0) setStep(2);
  };

  const payNow = () => {
    const e = validatePayment();
    setErrors(e);
    if (Object.keys(e).length === 0) {
      setStep(3);
      setTimeout(() => { onSuccess(orderId); }, 1200);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-head">
          <div className="modal-title">
            {step === 1 && "Delivery Details"}
            {step === 2 && "Payment Details"}
            {step === 3 && "Order Confirmed"}
          </div>
          <button className="cart-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          <div className="payment-steps">
            <div className={`pay-step ${step === 1 ? "active" : step > 1 ? "done" : ""}`}>Delivery</div>
            <div className={`pay-step ${step === 2 ? "active" : step > 2 ? "done" : ""}`}>Payment</div>
            <div className={`pay-step ${step === 3 ? "active" : ""}`}>Success</div>
          </div>

          {step === 1 && (
            <>
              <div className="form-g">
                <label className="form-l">Full Name</label>
                <input className={`form-i ${errors.name ? "err" : ""}`} value={form.name} onChange={(e) => updateForm("name", e.target.value)} placeholder="Enter your name" />
                {errors.name && <span className="err-msg">{errors.name}</span>}
              </div>

              <div className="form-row">
                <div className="form-g">
                  <label className="form-l">Email</label>
                  <input className={`form-i ${errors.email ? "err" : ""}`} value={form.email} onChange={(e) => updateForm("email", e.target.value)} placeholder="email@example.com" />
                  {errors.email && <span className="err-msg">{errors.email}</span>}
                </div>
                <div className="form-g">
                  <label className="form-l">Phone</label>
                  <input className={`form-i ${errors.phone ? "err" : ""}`} value={form.phone} onChange={(e) => updateForm("phone", e.target.value)} placeholder="10-digit mobile" />
                  {errors.phone && <span className="err-msg">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-g">
                <label className="form-l">Address</label>
                <textarea className={`form-i ${errors.address ? "err" : ""}`} value={form.address} onChange={(e) => updateForm("address", e.target.value)} placeholder="House, street, area" rows="3" />
                {errors.address && <span className="err-msg">{errors.address}</span>}
              </div>

              <div className="form-row">
                <div className="form-g">
                  <label className="form-l">City</label>
                  <input className={`form-i ${errors.city ? "err" : ""}`} value={form.city} onChange={(e) => updateForm("city", e.target.value)} placeholder="City" />
                  {errors.city && <span className="err-msg">{errors.city}</span>}
                </div>
                <div className="form-g">
                  <label className="form-l">PIN Code</label>
                  <input className={`form-i ${errors.pin ? "err" : ""}`} value={form.pin} onChange={(e) => updateForm("pin", e.target.value)} placeholder="PIN" />
                  {errors.pin && <span className="err-msg">{errors.pin}</span>}
                </div>
              </div>

              <div className="order-sum">
                <div className="order-sum-row"><span>Subtotal</span><span>₹{subtotal.toLocaleString()}</span></div>
                <div className="order-sum-row"><span>Delivery</span><span>{delivery === 0 ? "Free" : `₹${delivery}`}</span></div>
                <div className="order-sum-row"><span>Total</span><span>₹{total.toLocaleString()}</span></div>
              </div>

              <button className="modal-btn" onClick={nextDelivery}>Continue to Payment</button>
            </>
          )}

          {step === 2 && (
            <>
              {/* ── Payment method selector: UPI | Card | COD ── */}
              <div className="pay-methods">
                <div className={`pay-method ${method === "upi" ? "sel" : ""}`} onClick={() => setMethod("upi")}>
                  <span className="pay-method-icon">📱</span>
                  <span className="pay-method-label">UPI</span>
                </div>
                <div className={`pay-method ${method === "card" ? "sel" : ""}`} onClick={() => setMethod("card")}>
                  <span className="pay-method-icon">💳</span>
                  <span className="pay-method-label">Card</span>
                </div>
                <div className={`pay-method ${method === "cod" ? "sel" : ""}`} onClick={() => setMethod("cod")}>
                  <span className="pay-method-icon">🏠</span>
                  <span className="pay-method-label">Cash on Delivery</span>
                </div>
              </div>

              {method === "upi" && (
                <div className="form-g">
                  <label className="form-l">UPI ID</label>
                  <input className={`form-i ${errors.upi ? "err" : ""}`} value={form.upi} onChange={(e) => updateForm("upi", e.target.value)} placeholder="yourname@upi" />
                  {errors.upi && <span className="err-msg">{errors.upi}</span>}
                </div>
              )}

              {method === "card" && (
                <>
                  <div className="form-g">
                    <label className="form-l">Card Number</label>
                    <input className={`form-i ${errors.card ? "err" : ""}`} value={form.card} onChange={(e) => updateForm("card", e.target.value)} placeholder="XXXX XXXX XXXX XXXX" />
                    {errors.card && <span className="err-msg">{errors.card}</span>}
                  </div>
                  <div className="form-row">
                    <div className="form-g">
                      <label className="form-l">Expiry</label>
                      <input className={`form-i ${errors.expiry ? "err" : ""}`} value={form.expiry} onChange={(e) => updateForm("expiry", e.target.value)} placeholder="MM/YY" />
                      {errors.expiry && <span className="err-msg">{errors.expiry}</span>}
                    </div>
                    <div className="form-g">
                      <label className="form-l">CVV</label>
                      <input className={`form-i ${errors.cvv ? "err" : ""}`} value={form.cvv} onChange={(e) => updateForm("cvv", e.target.value)} placeholder="123" />
                      {errors.cvv && <span className="err-msg">{errors.cvv}</span>}
                    </div>
                  </div>
                </>
              )}

              {method === "cod" && (
                <div className="cod-info">
                  <div className="cod-info-icon">🏠</div>
                  <div>
                    <div className="cod-info-title">Cash on Delivery</div>
                    <div className="cod-info-desc">
                      Pay in cash when your order is delivered to your doorstep. Please keep exact change ready.
                      Our delivery partner will collect the payment at the time of delivery.<br /><br />
                      <strong style={{ color: "#9DCD43" }}>Note:</strong> COD orders may take 1–2 extra business days to process.
                    </div>
                  </div>
                </div>
              )}

              <div className="order-sum">
                <div className="order-sum-row">
                  <span>Payable Amount</span>
                  <span>₹{total.toLocaleString()}</span>
                </div>
                {method === "cod" && (
                  <div className="order-sum-row">
                    <span>Payment Mode</span>
                    <span style={{ color: "#B4E065" }}>Cash on Delivery</span>
                  </div>
                )}
              </div>

              <button className="modal-btn" onClick={payNow}>
                {method === "cod" ? `Place Order — Pay ₹${total.toLocaleString()} on Delivery` : `Pay ₹${total.toLocaleString()}`}
              </button>
              <button className="modal-btn-back" onClick={() => setStep(1)}>Back</button>
            </>
          )}

          {step === 3 && (
            <div className="success-wrap">
              <span className="success-icon">{method === "cod" ? "📦" : "✅"}</span>
              <h3 className="success-h">{method === "cod" ? "Order Placed!" : "Thank You!"}</h3>
              <p className="success-p">
                {method === "cod"
                  ? "Your Nari Samman order has been placed successfully. Please keep cash ready at the time of delivery. Our team will contact you to confirm the delivery slot."
                  : "Your Nari Samman order has been placed successfully. Our team will contact you shortly."}
              </p>
              <div className="order-id">Order ID: {orderId}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────
   NAVIGATION
─────────────────────────────────────────────── */

function Navigation({ current, setCurrent, cartCount, openCart }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => { setCurrent(id); setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <nav className={`nav ${scrolled ? "scrolled" : ""}`}>
      <div className="nav-inner">
        <div className="nav-logo" onClick={() => go("home")}>Nari <span>Samman</span></div>
        <button className="hamburger" onClick={() => setOpen(!open)}>☰</button>
        <ul className={`nav-links ${open ? "open" : ""}`}>
          {NAV_LINKS.map((link) => (
            <li key={link.id}>
              <button className={current === link.id ? "active" : ""} onClick={() => go(link.id)}>{link.label}</button>
            </li>
          ))}
        </ul>
        <button className="nav-cart-btn" onClick={openCart}>
          🛒 Cart
          <span className="cart-badge">{cartCount}</span>
        </button>
      </div>
    </nav>
  );
}

/* ───────────────────────────────────────────────
   HOME PAGE
─────────────────────────────────────────────── */

function HomePage({ setCurrent }) {
  return (
    <div className="page">
      <section className="hero">
        <div className="hero-bg" />
        <div className="hero-overlay" />
        <div className="hero-grid-bg" />
        <div className="hero-content">
          <div className="hero-inner">
            <div className="hero-layout">
              <div>
                <div className="hero-badge"><span />West Bengal · Women SHG Commerce</div>
                <h1 className="hero-h1">Nari<em>Samman</em></h1>
                <p className="hero-sub">A Bengal-inspired women empowerment marketplace connecting SHGs, tribal producers, Bengal weavers, food makers and handicraft artisans directly with modern buyers.</p>
                <p className="hero-quote">"From rural hands to national and global markets."</p>
                <div className="hero-cta-row">
                  <button className="btn-gold" onClick={() => setCurrent("products")}>Explore Products</button>
                  <button className="btn-glass" onClick={() => setCurrent("mission")}>View Mission</button>
                </div>
              </div>
              <div className="hero-stats">
                <div className="hero-stat"><div className="hero-stat-n">6K+</div><div className="hero-stat-l">Women and tribal SHGs envisioned in the ecosystem</div></div>
                <div className="hero-stat"><div className="hero-stat-n">29</div><div className="hero-stat-l">Product lines across honey, food, sarees and craft</div></div>
                <div className="hero-stat"><div className="hero-stat-n">4</div><div className="hero-stat-l">Marketplace product categories</div></div>
                <div className="hero-stat"><div className="hero-stat-n">WB</div><div className="hero-stat-l">West Bengal inspired visual identity</div></div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-strip">
          <div className="hero-strip-item"><div className="strip-icon">🍯</div><div className="strip-text"><strong>Sundarbans Honey</strong>Local honey images from your project folder.</div></div>
          <div className="hero-strip-item"><div className="strip-icon">🥻</div><div className="strip-text"><strong>Bengal Handloom</strong>Sarees and textile products inspired by Bengal heritage.</div></div>
          <div className="hero-strip-item"><div className="strip-icon">🎨</div><div className="strip-text"><strong>Tribal Craft</strong>Jute, bamboo, clay, terracotta and folk craft items.</div></div>
        </div>
      </section>

      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Project Summary" title="A women-led <em>Bengal commerce platform</em>" desc="Nari Samman combines rural production, product branding, warehousing, digital commerce, and women-led distribution into one integrated empowerment model." />
            <div className="sum-cards">
              <div className="glass-card sum-card"><div className="sum-card-icon">👩‍🌾</div><div className="sum-card-title">Source Empowerment</div><div className="sum-card-body">Women SHGs, tribal communities and artisans create products at village level with better visibility, packaging and market access.</div></div>
              <div className="glass-card sum-card"><div className="sum-card-icon">🛍️</div><div className="sum-card-title">Digital Marketplace</div><div className="sum-card-body">Product categories, cart system and checkout flow help convert local products into a modern web marketplace.</div></div>
              <div className="glass-card sum-card"><div className="sum-card-icon">🌾</div><div className="sum-card-title">Bengal Identity</div><div className="sum-card-body">The visual language uses dark navy depth and lime green accent inspired by the brand design system.</div></div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   ABOUT PAGE
─────────────────────────────────────────────── */

function AboutPage() {
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="About Nari Samman" title="Empowering women through <em>ethical rural commerce</em>" desc="Nari Samman is designed as a women empowerment initiative that links rural production, traditional knowledge, cultural craft, food processing, warehousing and digital selling." />
            <div className="vision-q">"Nari Samman is not only a marketplace. It is a dignity-first ecosystem where women producers are seen, valued and paid fairly."</div>
            <div className="two-col">
              <div className="box-green"><h3>Source Side Empowerment</h3><p>Women SHGs and tribal communities become producers, quality partners, local aggregators and brand contributors. The model helps them move beyond informal selling into a structured rural commerce system.</p></div>
              <div className="box-orange"><h3>Consumer Side Empowerment</h3><p>Women entrepreneurs, resellers, social commerce sellers and urban buyers can support these products through digital discovery, direct purchase and community-led distribution.</p></div>
            </div>
          </FadeIn>
        </div>
      </section>
      <section className="sec-alt">
        <div className="container">
          <FadeIn>
            <SH tag="Core Focus" title="What the platform <em>connects</em>" desc="The model connects products, producers, buyers, logistics and social impact in one digital system." />
            <div className="sum-cards">
              <div className="glass-card sum-card"><div className="sum-card-icon">🍯</div><div className="sum-card-title">Food Products</div><div className="sum-card-body">Honey, spices, tea, rice, pickles and food products linked with SHG-led processing and packaging.</div></div>
              <div className="glass-card sum-card"><div className="sum-card-icon">🧵</div><div className="sum-card-title">Weaving & Textile</div><div className="sum-card-body">Bengal sarees, handloom products, stoles and textile accessories connected to artisan revival.</div></div>
              <div className="glass-card sum-card"><div className="sum-card-icon">🎨</div><div className="sum-card-title">Rural Craft</div><div className="sum-card-body">Jute, terracotta, bamboo, clay, folk and tribal craft products for homes, gifting and ethical retail.</div></div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   MISSION PAGE
─────────────────────────────────────────────── */

function MissionPage() {
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Mission Objectives" title="Commerce with <em>dignity, access and ownership</em>" desc="The mission is to create a sustainable women-led rural commerce model that improves livelihood, protects cultural heritage and enables direct market participation." />
            <div className="mission-grid">
              {MISSION_OBJECTIVES.map((item, index) => (
                <div className="mission-item" key={item.text}>
                  <div className="mission-n">{String(index + 1).padStart(2, "0")}</div>
                  <div className="mission-t"><strong style={{ marginRight: 6 }}>{item.icon}</strong>{item.text}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   PILOT PAGE
─────────────────────────────────────────────── */

function PilotPage() {
  const pilotItems = [
    "Sandeshkhali pilot location", "Women SHG producer network",
    "Food, honey, handloom and craft focus", "Local aggregation model",
    "Warehouse-linked fulfilment", "Digital marketplace onboarding",
    "Skill and packaging support", "Producer-to-consumer visibility",
  ];
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Pilot Model" title="Sandeshkhali as the <em>first rural commerce hub</em>" desc="The pilot can demonstrate how rural products can be collected, quality-checked, packaged, listed online and distributed through a women-led commerce system." />
            <img className="section-img" src="https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?auto=format&fit=crop&w=1400&q=80" alt="Rural Bengal landscape" />
            <div className="pilot-chips">
              {pilotItems.map((item) => <div className="pilot-chip" key={item}>{item}</div>)}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   PRODUCTS PAGE
─────────────────────────────────────────────── */

function ProductsPage({ cart, addToCart }) {
  const [active, setActive] = useState(PRODUCT_CATEGORIES[0].id);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [addedId, setAddedId] = useState(null);

  const category = PRODUCT_CATEGORIES.find((cat) => cat.id === active);

  const addProduct = (product) => {
    const selectedOption = selectedOptions[product.id] || product.options?.[0] || "Standard";
    const selectedImage = product.optionImages?.[selectedOption] || product.img;
    addToCart({ ...product, img: selectedImage, selectedOption });
    setAddedId(product.id);
    setTimeout(() => setAddedId(null), 900);
  };

  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Product Marketplace" title="Bengal products with <em>dark navy premium cards</em>" desc="Honey uses your local images. Spices, sarees, handloom and craft use curated Unsplash images for authentic Bengal product presentation." />
            <div className="prod-tabs">
              {PRODUCT_CATEGORIES.map((cat) => (
                <button key={cat.id} className={`prod-tab ${active === cat.id ? "active" : ""}`} onClick={() => setActive(cat.id)}>
                  {cat.emoji} {cat.label}
                </button>
              ))}
            </div>

            <div style={{ background: "linear-gradient(135deg, rgba(157,205,67,0.08), rgba(180,224,101,0.06))", border: "1px solid rgba(157,205,67,0.16)", borderRadius: "24px", padding: "1.4rem 1.6rem", marginBottom: "1.8rem" }}>
              <div style={{ fontFamily: "'Syne', serif", fontSize: "1.55rem", fontWeight: 900, color: "#FFFFFF" }}>{category.emoji} {category.label}</div>
              <p style={{ color: "#8A94A8", lineHeight: 1.7, marginTop: 6 }}>Select product options, view related images and add items to the cart.</p>
            </div>

            <div className="prod-grid">
              {category.products.map((product) => {
                const selectedOption = selectedOptions[product.id] || product.options?.[0] || "Standard";
                const displayImage = product.optionImages?.[selectedOption] || product.img;
                return (
                  <div className="prod-card" key={product.id}>
                    <div className="prod-img-wrap">
                      <img src={displayImage} alt={product.name} />
                      <div className="prod-badge">{product.tag}</div>
                    </div>
                    <div className="prod-body">
                      <div>
                        <div className="prod-name">{product.name}</div>
                        <div className="prod-cat">{product.cat}</div>
                      </div>
                      <p className="prod-desc">{product.desc}</p>
                      <select className="prod-option" value={selectedOption} onChange={(e) => setSelectedOptions({ ...selectedOptions, [product.id]: e.target.value })}>
                        {(product.options || ["Standard"]).map((option) => <option key={option}>{option}</option>)}
                      </select>
                      <div className="prod-row">
                        <div className="prod-price">₹{product.price.toLocaleString()}</div>
                        <button className={`add-cart-btn ${addedId === product.id ? "added" : ""}`} onClick={() => addProduct(product)}>
                          {addedId === product.id ? "Added ✓" : "Add"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {cart.length > 0 && (
              <div style={{ marginTop: "2rem", background: "linear-gradient(135deg,#7AAF2A,#9DCD43)", color: "#131D29", borderRadius: "22px", padding: "1.3rem 1.5rem", display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", flexWrap: "wrap" }}>
                <strong>Cart contains {cart.reduce((sum, item) => sum + item.qty, 0)} item(s)</strong>
                <span>Continue shopping or open the cart from the top right.</span>
              </div>
            )}
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   DISTRIBUTION MODEL PAGE
─────────────────────────────────────────────── */

function ModelPage() {
  const flows = [
    { title: "Village Production", text: "Women SHGs, tribal communities, weavers and artisans create food, textile and craft products." },
    { title: "Collection & Aggregation", text: "Products are collected through local coordinators and moved into a structured warehouse pipeline." },
    { title: "Quality & Packaging", text: "Products are checked, sorted, packaged, labelled and prepared for digital or offline sale." },
    { title: "Digital Listing", text: "Products are listed on the Nari Samman marketplace with images, price, story and category identity." },
    { title: "Buyer Access", text: "Customers, women entrepreneurs, resellers and institutions can purchase directly through the platform." },
  ];
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Distribution Model" title="From <em>rural producer</em> to modern buyer" desc="The distribution model reduces dependency on middlemen by connecting production, quality control, storage, logistics and digital commerce." />
            <div className="two-col">
              <div>
                {flows.map((item, index) => (
                  <div className="flow-item" key={item.title}>
                    <div className="flow-n">{String(index + 1).padStart(2, "0")}</div>
                    <div className="flow-t"><strong>{item.title}</strong>{item.text}</div>
                  </div>
                ))}
              </div>
              <div className="box-green">
                <h3>Producer-to-Consumer Chain</h3>
                <p>The system supports women producers by giving them a direct route to consumers, institutions, bulk buyers and digital retail channels.</p>
                <br />
                <FList items={["Village-level production", "SHG-led aggregation", "Warehouse verification", "Digital product listing", "Customer purchase and delivery"]} />
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   ORGANISATION PAGE
─────────────────────────────────────────────── */

function StructurePage() {
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Organisation Structure" title="A participatory <em>entrepreneurship model</em>" desc="The organisation model can include working entrepreneurs, silent investors, local SHG leaders, field coordinators and digital commerce partners." />
            <div className="org-top">Nari Samman Rural Commerce & Women Empowerment Platform</div>
            <div className="org-chips">
              {["Women SHGs","Tribal Producers","Weavers","Artisans","Food Processing Units","Digital Partners","Investors"].map(c => <div className="org-chip" key={c}>{c}</div>)}
            </div>
            <div className="entrepn-grid">
              <div className="entrepn-card">
                <span className="ebadge ebadge-g">Working Entrepreneurs</span>
                <div className="epct" style={{ color: "#9DCD43" }}>50%</div>
                <p style={{ color: "#8A94A8", lineHeight: 1.8 }}>Working entrepreneurs may actively handle sourcing, production coordination, sales, logistics, technology, marketing or regional growth.</p>
              </div>
              <div className="entrepn-card">
                <span className="ebadge ebadge-o">Silent Investors</span>
                <div className="epct" style={{ color: "#B4E065" }}>50%</div>
                <p style={{ color: "#8A94A8", lineHeight: 1.8 }}>Silent investors may support capital needs, infrastructure, branding and expansion while allowing the operational team to manage execution.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   WAREHOUSE PAGE
─────────────────────────────────────────────── */

function WarehousePage() {
  const channels = ["SHG product aggregation","Quality testing and grading","Packaging and labelling","Inventory storage","Dispatch coordination","Bulk order fulfilment","E-commerce delivery support"];
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Warehouse & Logistics" title="A central hub for <em>quality and fulfilment</em>" desc="The warehouse system can become the operational backbone of Nari Samman, supporting aggregation, packaging, inventory and delivery." />
            <img className="section-img" src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1400&q=80" alt="Warehouse logistics" />
            <div className="two-col">
              <div>
                {channels.map((item, index) => (
                  <div className="channel-row" key={item}>
                    <div className="channel-icon">📦</div>
                    <div className="channel-l">{item}</div>
                    <div className="ch-num">{String(index + 1).padStart(2, "0")}</div>
                  </div>
                ))}
              </div>
              <div className="box-orange">
                <h3>Operational Benefit</h3>
                <p>A warehouse gives small producers a professional bridge to modern markets by solving storage, packaging, product uniformity, dispatch and order handling.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   E-COMMERCE PAGE
─────────────────────────────────────────────── */

function EcommercePage() {
  const channels = ["Product discovery","Category-based browsing","Cart and checkout","Digital enquiry flow","Women-led social commerce","Future payment gateway","Bulk and institutional sales"];
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="E-Commerce" title="A functional <em>digital marketplace</em>" desc="The web app is designed to support product listing, product images, product options, cart system and checkout flow." />
            <div className="two-col">
              <div className="box-green">
                <h3>Digital Platform Features</h3>
                <p>The platform connects rural products with buyers using a simple marketplace interface. Future versions can connect Razorpay, Stripe, WhatsApp ordering, admin dashboards and inventory APIs.</p>
              </div>
              <div>
                {channels.map((item, index) => (
                  <div className="channel-row" key={item}>
                    <div className="channel-icon">🛒</div>
                    <div className="channel-l">{item}</div>
                    <div className="ch-num">{String(index + 1).padStart(2, "0")}</div>
                  </div>
                ))}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   WEAVING PAGE
─────────────────────────────────────────────── */

function WeavingPage() {
  const focusItems = ["Tant saree revival","Baluchari and silk storytelling traditions","Kantha embroidery-based product lines","Tribal textile and stole collections","Quality grading and artisan certification","Online catalogue for handloom products"];
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Weaving Revival" title="Reviving Bengal's <em>handloom heritage</em>" desc="Nari Samman can support Bengal weavers by creating a digital market for sarees, stoles, textiles and heritage-inspired products." />
            <div className="weave-img-grid">
              <img src="https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&w=1000&q=80" alt="Bengal saree weaving" />
              <img src="https://images.unsplash.com/photo-1602573991155-21f0143bb45c?auto=format&fit=crop&w=700&q=80" alt="Handwoven cloth" />
              <img src="https://images.unsplash.com/photo-1590735213920-68192a487bc2?auto=format&fit=crop&w=700&q=80" alt="Kantha embroidery" />
            </div>
            <div className="weave-stat-row">
              <div className="weave-s"><div className="big">80%</div><div className="lbl">Traditional weaving families need stronger market access</div></div>
              <div className="weave-s"><div className="big">10+</div><div className="lbl">Textile and handloom product opportunities</div></div>
              <div className="weave-s"><div className="big">WB</div><div className="lbl">Bengal identity-led product branding</div></div>
            </div>
            <div className="two-col">
              <div>
                {focusItems.map((item, index) => (
                  <div className="focus-item" key={item}>
                    <div className="focus-n">{String(index + 1).padStart(2, "0")}</div>
                    <div className="focus-l">{item}</div>
                  </div>
                ))}
              </div>
              <div className="box-green">
                <h3>Handloom Opportunity</h3>
                <p>Bengal handloom products can be sold through curated storytelling, better images, product authentication, artisan profiles, gifting packs and digital catalogues.</p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   FINANCE PAGE
─────────────────────────────────────────────── */

function FinancePage() {
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Finance" title="Funding pathways for <em>rural commerce scale</em>" desc="The project can combine entrepreneur equity, institutional finance, government schemes and rural development support." />
            <div className="fin-grid">
              {FUNDING_SOURCES.map((source) => (
                <div className="fin-card" key={source.label}>
                  <div className="fin-head" style={{ color: source.color }}>{source.label}</div>
                  <FList items={source.items} />
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   REVENUE PAGE
─────────────────────────────────────────────── */

function RevenuePage() {
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Revenue Streams" title="A sustainable <em>income model</em>" desc="The platform can generate revenue from direct product sales, commissions, services, memberships, wholesale and export-oriented opportunities." />
            <div className="rev-grid">
              {REVENUE_STREAMS.map((stream, index) => (
                <div className="rev-card" key={stream.label}>
                  <div className="rev-icon">{stream.icon}</div>
                  <div>
                    <div className="rev-num">{String(index + 1).padStart(2, "0")}</div>
                    <div className="rev-label">{stream.label}</div>
                    <div className="rev-desc">{stream.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   TEAM PAGE
─────────────────────────────────────────────── */

function TeamPage() {
  return (
    <div className="page">
      <section className="sec">
        <div className="container">
          <FadeIn>
            <SH tag="Contributors" title="People behind <em>Nari Samman</em>" desc="A multidisciplinary contributor group supporting rural empowerment, weaving, food processing, e-commerce and finance." />
            <div className="team-grid">
              {KEY_CONTRIBUTORS.map((member) => (
                <div className="team-card" key={member.name}>
                  <img src={member.img} alt={member.name} className="team-av" />
                  <div>
                    <div className="team-name">{member.name}</div>
                    <div className="team-role">{member.role}</div>
                    <div className="team-txt">{member.contribution}</div>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────────────────────────
   CONTACT / CTA SECTION
─────────────────────────────────────────────── */

function ContactCTA() {
  const [submitted, setSubmitted] = useState(false);
  const submitForm = (e) => { e.preventDefault(); setSubmitted(true); };

  return (
    <section className="cta-sec">
      <div className="cta-inner">
        {!submitted ? (
          <>
            <h2>Join the Nari Samman Movement</h2>
            <p>Connect with us as a buyer, partner, SHG member, investor, artisan, distributor or digital commerce supporter.</p>
            <form className="cta-form" onSubmit={submitForm}>
              <div className="cform-row">
                <div className="form-g"><label className="form-l">Name</label><input className="form-i" placeholder="Your full name" required /></div>
                <div className="form-g"><label className="form-l">Phone</label><input className="form-i" placeholder="Your phone number" required /></div>
              </div>
              <div className="cform-row">
                <div className="form-g"><label className="form-l">Email</label><input type="email" className="form-i" placeholder="you@example.com" required /></div>
                <div className="form-g">
                  <label className="form-l">Interest</label>
                  <select className="form-i" required>
                    <option value="">Select interest</option>
                    <option>Buyer</option><option>SHG Member</option><option>Artisan</option>
                    <option>Investor</option><option>Distributor</option><option>Technology Partner</option>
                  </select>
                </div>
              </div>
              <div className="form-g"><label className="form-l">Message</label><textarea className="form-i" rows="4" placeholder="Tell us how you want to participate" /></div>
              <button className="form-submit" type="submit">Submit Interest</button>
            </form>
          </>
        ) : (
          <div className="success-banner">
            <h3>Thank you!</h3>
            <p>Your interest has been submitted. The Nari Samman team will connect with you soon.</p>
          </div>
        )}
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────────
   FOOTER
─────────────────────────────────────────────── */

function Footer({ setCurrent }) {
  const go = (id) => { setCurrent(id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  return (
    <footer>
      <div className="foot-grid">
        <div>
          <div className="foot-logo">Nari Samman</div>
          <div className="foot-tag">Empowering Women from Source to Consumer</div>
          <p className="foot-desc">A women empowerment marketplace inspired by West Bengal, connecting SHGs, tribal communities, artisans, weavers, food makers and buyers through ethical digital commerce.</p>
        </div>
        <div className="foot-col">
          <h4>Platform</h4>
          <ul>
            <li><button onClick={() => go("products")}>Products</button></li>
            <li><button onClick={() => go("ecommerce")}>E-Commerce</button></li>
            <li><button onClick={() => go("warehouse")}>Warehouse</button></li>
            <li><button onClick={() => go("model")}>Distribution</button></li>
          </ul>
        </div>
        <div className="foot-col">
          <h4>Mission</h4>
          <ul>
            <li><button onClick={() => go("mission")}>Objectives</button></li>
            <li><button onClick={() => go("pilot")}>Pilot</button></li>
            <li><button onClick={() => go("weaving")}>Weaving</button></li>
            <li><button onClick={() => go("finance")}>Finance</button></li>
          </ul>
        </div>
        <div className="foot-col">
          <h4>Organisation</h4>
          <ul>
            <li><button onClick={() => go("structure")}>Structure</button></li>
            <li><button onClick={() => go("revenue")}>Revenue</button></li>
            <li><button onClick={() => go("team")}>Team</button></li>
            <li><button onClick={() => go("about")}>About</button></li>
          </ul>
        </div>
      </div>
      <div className="foot-bottom">
        <span>© 2026 Nari Samman. All rights reserved.</span>
        <span>West Bengal · SHG Commerce · Women Empowerment</span>
      </div>
    </footer>
  );
}

/* ───────────────────────────────────────────────
   MAIN APP
─────────────────────────────────────────────── */

export default function App() {
  const [current, setCurrent] = useState("home");
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  const addToCart = (product) => {
    setCart((items) => {
      const selectedOption = product.selectedOption || "Standard";
      const uniqueId = `${product.id}-${selectedOption}`;
      const existing = items.find((item) => item.uniqueId === uniqueId);
      if (existing) {
        return items.map((item) =>
          item.uniqueId === uniqueId ? { ...item, qty: item.qty + 1 } : item
        );
      }
      return [...items, { ...product, uniqueId, qty: 1 }];
    });
  };

  const checkout = () => { setCartOpen(false); setPaymentOpen(true); };
  const handlePaymentSuccess = () => { setCart([]); setPaymentOpen(false); };

  const pages = {
    home: <HomePage setCurrent={setCurrent} />,
    about: <AboutPage />,
    mission: <MissionPage />,
    pilot: <PilotPage />,
    products: <ProductsPage cart={cart} addToCart={addToCart} />,
    model: <ModelPage />,
    structure: <StructurePage />,
    warehouse: <WarehousePage />,
    ecommerce: <EcommercePage />,
    weaving: <WeavingPage />,
    finance: <FinancePage />,
    revenue: <RevenuePage />,
    team: <TeamPage />,
  };

  return (
    <>
      <style>{S}</style>
      <Navigation current={current} setCurrent={setCurrent} cartCount={cartCount} openCart={() => setCartOpen(true)} />
      {pages[current]}
      <ContactCTA />
      <Footer setCurrent={setCurrent} />
      {cartOpen && <CartDrawer cart={cart} setCart={setCart} onClose={() => setCartOpen(false)} onCheckout={checkout} />}
      {paymentOpen && <PaymentModal cart={cart} onClose={() => setPaymentOpen(false)} onSuccess={handlePaymentSuccess} />}
    </>
  );
}